import { useRef } from "react";
import {
  insertRichTextLink,
  prefixRichTextLine,
  wrapRichTextSelection,
} from "@/lib/document-builder/blocks/richTextSubset";
import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import { DOC_BLOCK_TYPE_LABELS } from "@/lib/document-builder/blocks/blockTypes";
import { DocumentBlockFields } from "./DocumentBlockFields";

type Props = {
  block: DocBlock;
  assets: DocAsset[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  canDelete: boolean;
  onChange: (block: DocBlock) => void;
  onMove: (direction: "up" | "down") => void;
  onDelete: () => void;
};

export function DocumentBlockRow(props: Props) {
  const { block, assets, canMoveUp, canMoveDown, canDelete, onChange, onMove, onDelete } =
    props;
  const richTextRef = useRef<HTMLTextAreaElement>(null);

  function applyRichTextEdit(
    fn: (value: string, start: number, end: number) => { next: string; cursor: number },
  ) {
    if (block.type !== "rich_text") return;
    const el = richTextRef.current;
    const start = el?.selectionStart ?? block.body.length;
    const end = el?.selectionEnd ?? block.body.length;
    const { next, cursor } = fn(block.body, start, end);
    onChange({ ...block, body: next });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(cursor, cursor);
    });
  }

  return (
    <section className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 dark:border-zinc-800">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-zinc-500">
          {DOC_BLOCK_TYPE_LABELS[block.type]}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            disabled={!canMoveUp}
            onClick={() => onMove("up")}
            className="rounded border border-gray-300 px-2 py-0.5 text-[10px] disabled:opacity-40 dark:border-zinc-600"
          >
            ↑
          </button>
          <button
            type="button"
            disabled={!canMoveDown}
            onClick={() => onMove("down")}
            className="rounded border border-gray-300 px-2 py-0.5 text-[10px] disabled:opacity-40 dark:border-zinc-600"
          >
            ↓
          </button>
          {canDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="rounded border border-red-300 px-2 py-0.5 text-[10px] text-red-700 dark:border-red-800 dark:text-red-300"
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>
      <div className="px-3 py-3">
        <DocumentBlockFields
          block={block}
          assets={assets}
          richTextRef={richTextRef}
          onChange={onChange}
          onRichTextWrap={(wrap) => applyRichTextEdit((v, s, e) => wrapRichTextSelection(v, s, e, wrap))}
          onRichTextPrefix={(prefix) =>
            applyRichTextEdit((v, s) => prefixRichTextLine(v, s, prefix))
          }
          onRichTextLink={() => applyRichTextEdit(insertRichTextLink)}
        />
      </div>
    </section>
  );
}
